import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import GridViewIcon from '@mui/icons-material/GridView';
import ViewListIcon from '@mui/icons-material/ViewList';
import { JOB_PORTAL_KEYS, NS } from '../../../../i18n/constants/keys';
import type { JobPostViewMode } from '../interfaces';

interface IJobListControlsProps {
  jobCount: number;
  maxPages: number;
  pageNumber: number;
  isLoading: boolean;
  viewMode: JobPostViewMode;
  onViewModeChange: (mode: JobPostViewMode) => void;
  activeFilterCount: number;
}

export const JobListControls = ({
  jobCount,
  maxPages,
  pageNumber,
  isLoading,
  viewMode,
  onViewModeChange,
  activeFilterCount,
}: IJobListControlsProps) => {
  const { t } = useTranslation(NS.jobPortal);

  return (
    <Paper sx={{ mb: 2 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        alignItems={{ sm: 'center' }}
        justifyContent="space-between"
        sx={{ px: 2, py: 1.5 }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Stack direction="row" spacing={1} alignItems="center">
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'success.main' }} />
            <Typography variant="body2" fontWeight={600}>
              {t(JOB_PORTAL_KEYS.listing.showing)}{' '}
              <Typography component="span" variant="body2" fontWeight={800} color="primary">
                {jobCount}
              </Typography>{' '}
              {t(JOB_PORTAL_KEYS.listing.jobs)}
              {maxPages > 1 && (
                <Typography component="span" variant="body2" color="text.secondary">
                  {' '}
                  ({t(JOB_PORTAL_KEYS.listing.page)} {pageNumber} {t(JOB_PORTAL_KEYS.listing.of)} {maxPages})
                </Typography>
              )}
            </Typography>
          </Stack>
          {isLoading && (
            <Chip
              icon={<CircularProgress size={14} color="inherit" />}
              label={t(JOB_PORTAL_KEYS.listing.loading)}
              size="small"
              color="success"
              sx={{ fontWeight: 600 }}
            />
          )}
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="caption" fontWeight={700}>
            {t(JOB_PORTAL_KEYS.listing.view)}:
          </Typography>
          <Tabs
            value={viewMode}
            onChange={(_e, value: JobPostViewMode) => onViewModeChange(value)}
            aria-label="Job list view"
            sx={{ minHeight: 32 }}
          >
            <Tab
              value="grid"
              label={t(JOB_PORTAL_KEYS.listing.grid)}
              icon={<GridViewIcon fontSize="small" />}
              iconPosition="start"
              sx={{ minHeight: 32, minWidth: 44 }}
            />
            <Tab
              value="list"
              label={t(JOB_PORTAL_KEYS.listing.list)}
              icon={<ViewListIcon fontSize="small" />}
              iconPosition="start"
              sx={{ minHeight: 32, minWidth: 44 }}
            />
          </Tabs>
        </Stack>
      </Stack>
      {activeFilterCount > 0 && (
        <Box sx={{ borderTop: 1, borderColor: 'divider', px: 2, py: 1 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            {activeFilterCount} filter{activeFilterCount === 1 ? '' : 's'} applied
          </Typography>
        </Box>
      )}
    </Paper>
  );
};
